<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { headerContainerHeight, headerContainerHeightFixed, mobileNavigationItems } from '@/io'
import HeaderBranding from '~/components/io/io-header/HeaderBranding.vue'
import MenuTrigger from '@/components/io/io-flyout/MenuTrigger.vue'
import FlyoutMenu from '@/components/io/io-flyout/FlyoutMenu.vue'
import FlyoutMenuItem from '@/components/io/io-flyout/FlyoutMenuItem.vue'
import { useContentStore } from '@/stores/content'

import LogoFlyout from '~/components/branding/LogoFlyout.vue'



const isMounted = useMounted()
const { $io } = useNuxtApp()

const headerFixed = computed(() => $io.headerFixed)
const mobileMenuActive = computed(() => $io.mobileMenuActive)

const router = useRouter()
const route = useRoute()


const contentStore = useContentStore()
const productCategories = computed(() => contentStore.productCategories ?? [])

const menu = computed(() => {
  const categories = productCategories.value

  return mobileNavigationItems.map((item) => {
    if (item.name === 'products') {
      const categoryChildren = categories.map((category) => ({
        label: category.name,
        name: `products-${category.slug}`,
        type: 'route' as const,
        routePath: `/products/${category.slug}`,
        activeRouteName: `products-${category.slug}`,
        activeRouteContainsName: true,
        children: [],
      }))

      return {
        ...item,
        children: [...(item.children ?? []), ...categoryChildren],
      }
    }

    return {
      ...item,
      children: item.children ?? [],
    }
  })
})

// Track which parent menu is expanded (if any)
const expandedParent = ref<string | null>(null)

// Toggle the mobile nav flyout active state if needed.
function handleToggle() {
  $io.mobileNavFlyoutActive = !$io.mobileNavFlyoutActive
}

function closeFlyout() {
  $io.mobileMenuActive = false
}

// Handle clicks on a parent menu item.
// If the item has children, toggle its expansion.
// Otherwise, navigate (or perform an action) and close the flyout.
function handleItemClick(item: MenuItemSettings, close: () => void) {
  if (item.children && item.children.length) {
    handleParentToggle(item.name)
  } else {
    if (item.type === 'route' && item.routePath) {
      router.push(item.routePath)
    } else if (item.type === 'action' && item.action) {
      item.action()
    }
    close()
    closeFlyout()
  }
}

// Active state helper: checks if a menu item is active
function isActive(item: MenuItemSettings): boolean {
  if (item.routePath === route.path || item.name === route.name) {
    return true;
  }
  if (item.children && item.children.length) {
    return item.children.some(child => child.name === route.name || child.routePath === route.path);
  }
  return false;
}

// Active state for child items.
function isChildActive(child: MenuItemSettings): boolean {
  return child.name === route.name || child.routePath === route.path;
}

function isParent(item: MenuItemSettings): boolean {
  return !!(item.children && item.children.length > 0)
}

function handleParentToggle(name: string) {
  expandedParent.value = expandedParent.value === name ? null : name
}

</script>

<template>
  <Frame v-if="isMounted" class="sec-mobile-navigation" w="grid grid-cols-[1fr_60px] mw-92 mx-auto h-full">
    <Frame class="sec-branding" w="w-full h-full">
      <HeaderBranding />
    </Frame>
    <Frame class="sec-response-toggle relative z-10">
      <Popover v-if="isMounted" ref="popover" v-slot="{ open, close }" class="sec-flyout-popover relative">
        <PopoverButton class="btn-flyout-trigger" :class="{ active: open }">
          <MenuTrigger :active="open" :fixed="headerFixed" @toggle="handleToggle" />
        </PopoverButton>

        <transition name="flyout-slide">
          <PopoverPanel key="flyout-menu" class="sec-flyout-menu" :class="{ active: open }">
            <div class="sec-flyout-menu-inner">
              <!-- Use the headlessui close function directly -->
              <button class="btn-close" @click="() => { close(); closeFlyout() }">
                <slot name="close-btn-icon">
                  <i class="flex i i-mdi:close" />
                </slot>
              </button>
              <div class="sec-navigation-wrapper">
                <div class="sec-flyout-header flex justify-center items-center gpt-3 pmb-1">
                  <LogoFlyout />
                </div>
                <div class="sec-flyout-container overflow-auto">
                  <slot name="flyoutprimary">
                    <FlyoutMenu>
                      <template #default>
                        <div v-for="item in menu" :key="item.name">
                          <FlyoutMenuItem :label="item.label" :name="item.name" :parent="isParent(item)"
                            :expanded="expandedParent === item.name" :active="isActive(item)"
                            @click="handleItemClick(item, close)"
                            @toggle-parent="() => handleParentToggle(item.name)">
                            <template v-if="isParent(item)">
                              <FlyoutMenuItem v-for="sub in item?.children" :key="sub.name" :label="sub.label"
                                :name="sub.name" :parent="false" :expanded="false" :active="isActive(sub)"
                                @click="() => handleItemClick(sub, close)" />
                            </template>
                          </FlyoutMenuItem>
                        </div>
                      </template>
                    </FlyoutMenu>
                  </slot>
                </div>
                <div v-if="$slots.footer" class="sec-flyout-footer">
                  <slot name="footer"></slot>
                </div>
              </div>
            </div>
          </PopoverPanel>
        </transition>
      </Popover>
    </Frame>
  </Frame>
</template>

<style lang="scss" scoped>
.sec-mobile-navigation {
  width: 100%;
  height: v-bind(headerContainerHeight);
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.5s ease;

  :deep(.btn-flyout-trigger) {
    border: 0;
    background: transparent;
    outline: 1px;
  }

  .sec-response-toggle {
    display: flex;
    align-items: center;
  }

  .sec-branding {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  &.fixed {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: auto !important;
    z-index: 100 !important;
    height: v-bind(headerContainerHeightFixed);
    background: white;
    @apply shadow-sm;
  }
}

.sec-flyout-menu {
  background: white;
  position: fixed;
  top: 0;
  width: 92%;
  left: 0;
  height: 100%;
  z-index: 20;

  @include mq($from: mlx) {
    max-width: 400px;
  }

  @apply shadow-lg;

  .sec-flyout-menu-inner {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .btn-close {
      position: absolute;
      top: 0;
      right: 0;
      width: 50px !important;
      height: 50px !important;
      z-index: 20;
      font-size: fs(2);
      color: var(--base-650);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;

      @include mq($from: txs) {
        display: none;
      }
    }

    .sec-navigation-wrapper {
      width: 100%;
      display: grid;
      gap: gr(1);

      >.sec-flyout-header {
        >.sec-logo {
          padding-top: gr(1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;

          >img {
            transition: all 0.2s ease;
            height: 50px;
          }
        }
      }

      >.sec-flyout-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;

        .flyout-menu-item {

          :deep(>button.label) {
            transition: all 0 ease;
            border: 0;
            background: transparent;

            >span {
              color: var(--base-600);
            }

            &:active {
              /* background: var(--base-50); */
            }
          }

          &.active {
            :deep(>button.label) {
              font-weight: 500;
              border-radius: gr(0.2);
              color: var(--c-sky-blue);

              >span {
                color: var(--c-sky-blue);
              }
            }
          }
        }
      }

      >.sec-flyout-footer {}
    }
  }
}

/* New transition for submenu items */
.submenu-slide-enter-active,
.submenu-slide-leave-active {
  transition: all 0.3s ease;
}

.submenu-slide-enter-from,
.submenu-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.submenu-slide-enter-to,
.submenu-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.sec-flyout-popover {
  @include mq($from: d) {
    display: none;
  }

  .sec-mobile-nav-trigger {
    position: relative;
    align-items: center;

    @include mq($from: ds) {
      display: none;
    }
  }
}

.active>a,
.active>button {
  font-weight: bold;
  color: #007bff;
}
</style>
